import { CompanyListItem, CompanyReachDTO } from 'types/company';
import { useCompanyOrCommunityDashboardData } from 'pages/Dashboard/useDashboardData';
import { Hidden } from '@mui/material';
import { bluePlanetTheme } from 'ui/theme';
import { useCompanyUserSummary } from 'apis/CompanyAPI/companies/useCompanyUserSummary';
import { getOrUndefined } from 'util/resource';
import useResource from 'util/resource/useResource';
import { companiesAPIUrls } from 'apis/CompanyAPI/companies/companiesApi';
import Resource from 'util/resource/Resource';
import Card from 'ui/views/cards/Card';
import { getReachText } from 'pages/Company/Insights';
import { Link } from 'react-router-dom';
import { companyUrls, userUrls } from 'urls';
import AddIcon from 'ui/elements/icons/AddIcon';
import TurnRightIcon from 'ui/elements/icons/TurnRightIcon';
import OnYourCompanyAgenda from 'pages/Dashboard/manage/OnYourCompanyAgenda';
import CompanyInsights from 'pages/Dashboard/manage/CompanyInsightsCards';
import Todos from 'pages/Dashboard/manage/todos/Todos';
import { MobileMargin } from 'ui/views/layouts/BaseLayout';
import FounderPulse from 'pages/Dashboard/manage/FounderPulse';
import CompanyInsightsSidebar from 'pages/Dashboard/manage/CompanyInsightsSidebar';
import React from 'react';
import styled from '@emotion/styled';
import { sectionSpacing } from 'ui/theme/themeConstants';
import { ActionItem, ActionLinks } from 'pages/Dashboard/manage/ManageCommunity';
import MatchIcon from 'ui/elements/icons/MatchIcon';
import ShareIcon2 from 'ui/elements/icons/ShareIcon2';
import PenIcon from 'ui/elements/icons/PenIcon';
import SaturnIcon from 'ui/elements/icons/SaturnIcon';
import GraphIcon from 'ui/elements/icons/GraphIcon';
import MoneyReceiveSquareIcon from 'ui/elements/icons/MoneyReceiveSquareIcon';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { useUiState } from 'apis/CompanyAPI/users/useUiState';
import useLazyResource from 'util/resource/useLazyResource';
import { FeedbackContext } from 'types/user';
import { usersApi } from 'apis/CompanyAPI/users/usersApi';
import classNames from 'classnames';
import KpiAPI from 'apis/KpiAPI';
import { KpiWithDisplay } from 'domain/kpis/types';
import KpiBulkUpdateDialog from 'domain/kpis/KpiBulkUpdateDialog';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

const Grid = styled.div`
  display: grid;
  column-gap: ${sectionSpacing};
  grid-template-columns: 1fr 300px;
  grid-template-areas: 'main sidebar';

  ${bluePlanetTheme.breakpoints.down('lg')} {
    display: flex;
    gap: ${sectionSpacing};
  }
`;

export default function ManageCompany({ company }: { company: CompanyListItem }) {
  const location = useLocation();
  const history = useHistory();
  const { resource: dashboardData } = useCompanyOrCommunityDashboardData(company);
  const { isMobile, isLgDown } = useBreakpoints();
  const companyUserSummary = useCompanyUserSummary(company?.id);
  const companyUsers = getOrUndefined(companyUserSummary.resource);

  // Check URL params for dialog state
  const searchParams = new URLSearchParams(location.search);
  const editKpisParam = searchParams.get('editKpis');
  // Sync state directly with URL params (no useEffect needed)
  const isKpiDialogOpen = editKpisParam === 'true';

  const { resource: reachResource } = useResource<CompanyReachDTO>(companiesAPIUrls.insights.reach(company.id));
  const { resource: kpisResource, mutate: mutateKpis } = useResource<KpiWithDisplay[]>(KpiAPI.kpi.list(company.id));

  const reach = getOrUndefined(reachResource);
  const kpis = getOrUndefined(kpisResource) || [];

  const handleCloseKpiDialog = () => {
    // Remove URL param (this will cause isKpiDialogOpen to become false)
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.delete('editKpis');
    history.replace({ search: newSearchParams.toString() });
  };

  const { uiState, saveUiState } = useUiState();
  const actualUiState = getOrUndefined(uiState);
  const [sendFeedback] = useLazyResource(
    (feedbackContext: FeedbackContext) =>
      usersApi.feedback({
        feedback: undefined,
        anonymous: false,
        feedbackContext: feedbackContext,
        companyId: company.id,
      }),
    {
      onSuccess: () => {
        if (uiState.state === 'fetched') {
          saveUiState({
            feedbackSent: { ...actualUiState?.feedbackSent, interestedInNetworkBuilding: true },
          });
        }
      },
      onFailure: () => {},
    },
  );

  return (
    <>
      <Resource resource={dashboardData}>
        {dashboardData => (
          <>
            <div>
              {reach && (
                <div className="u-flex u-flex--gap-quarter u-content-spacing-bottom u-flex--wrap">
                  <Card
                    className={classNames('text-small u-flex-align-center u-flex--gap-1 u-flex--wrap', {
                      'u-flex-grow': isLgDown,
                    })}
                  >
                    <span>
                      Company connections <strong className="text-weight-medium">{reach.connectionCount}</strong>
                    </span>
                    <span>
                      Potential reach{' '}
                      <strong>
                        {reach.potentialReach ? (
                          <strong className="text-weight-medium">{getReachText(reach.potentialReach)}</strong>
                        ) : (
                          <strong className="text-weight-medium">{reach.connectionCount}</strong>
                        )}
                      </strong>
                    </span>
                  </Card>
                  <Card className="u-flex-grow u-flex u-flex-align-center u-flex-space-between u-flex--wrap u-flex--gap-1">
                    <div className="u-flex u-flex-align-center u-flex--wrap">
                      <span className="text-body">Increase your reach:</span>
                      <Link className="text-link u-half-spacing-left" to={`/communities/investor-matching`}>
                        <MatchIcon className="u-quarter-spacing-right" fontSize="small" /> Get investor matching
                      </Link>
                      , &nbsp;
                      <Link className="text-link" to={companyUrls.settings.inviteUser(company.slug)}>
                        <AddIcon className="u-quarter-spacing-right" fontSize="small" /> Invite your network
                      </Link>
                      , or&nbsp;
                      <Link className="text-link" to={companyUrls.settings.share(company.slug)}>
                        <ShareIcon2 fontSize="small" /> share your profile
                      </Link>
                    </div>

                    <Link className="text-body text-link-grey" to={companyUrls.overview(company.slug, 'profile')}>
                      <TurnRightIcon className="u-half-spacing-right" fontSize="small" />
                      Go to company profile
                    </Link>
                  </Card>
                </div>
              )}
              <MobileMargin className="u-content-spacing-bottom">
                <ActionLinks>
                  <ActionItem
                    color="blue"
                    to={companyUrls.overview(company.slug, 'updates', {
                      isPostingUpdate: true,
                    })}
                  >
                    <PenIcon className="u-half-spacing-right" fontSize="small"></PenIcon>
                    Write an update
                  </ActionItem>
                  <ActionItem to={`/manage?editKpis=true`}>
                    <GraphIcon className="u-half-spacing-right" fontSize="small" />
                    Update your KPIs
                  </ActionItem>

                  <ActionItem to={companyUrls.settings.fundingRound(company.slug)}>
                    <MoneyReceiveSquareIcon className="u-half-spacing-right" fontSize="small" />
                    Prepare a funding round
                  </ActionItem>
                  <ActionItem to={`/communities/investor-matching`}>
                    <MatchIcon className="u-half-spacing-right" fontSize="small" /> Get investor matching
                  </ActionItem>
                  <ActionItem
                    onClick={() => {
                      if (
                        uiState.state === 'fetched' &&
                        actualUiState?.feedbackSent?.interestedInNetworkBuilding !== true
                      ) {
                        sendFeedback('InterestedInNetworkBuilding');
                      }
                    }}
                    to={userUrls.discover}
                  >
                    <SaturnIcon className="u-half-spacing-right" />
                    Build network
                  </ActionItem>
                </ActionLinks>
              </MobileMargin>
            </div>
            <div>
              <OnYourCompanyAgenda
                messages={dashboardData.messages.filter(
                  it =>
                    ![
                      'welcome-to-company-profile-as-new-company-admin',
                      'should-update-investors-as-company-admin',
                      'should-update-company-profile-as-company-admin',
                      'mentor-with-no-mentees',
                    ].includes(it.type),
                )} // We show these in to-do's instead
              />
            </div>
            <CompanyInsights company={company} />
            {isMobile ? (
              <>
                <div style={{ gridArea: 'main' }}>
                  {companyUsers && (
                    <Todos messages={dashboardData.messages} userSummary={companyUsers} company={company} />
                  )}
                  <MobileMargin>
                    <FounderPulse company={company} />
                  </MobileMargin>
                </div>
                <Hidden lgDown>
                  <div style={{ gridArea: 'sidebar' }}>
                    <CompanyInsightsSidebar company={company} />
                  </div>
                </Hidden>
              </>
            ) : (
              <Grid>
                <div style={{ gridArea: 'main' }}>
                  {companyUsers && (
                    <Todos messages={dashboardData.messages} userSummary={companyUsers} company={company} />
                  )}
                  <FounderPulse company={company} />
                </div>
                <Hidden lgDown>
                  <div style={{ gridArea: 'sidebar' }}>
                    <CompanyInsightsSidebar company={company} />
                  </div>
                </Hidden>
              </Grid>
            )}
          </>
        )}
      </Resource>

      {isKpiDialogOpen && (
        <KpiBulkUpdateDialog
          kpis={kpis}
          companyId={company.id}
          companySlug={company.slug}
          isOpen={isKpiDialogOpen}
          onClose={handleCloseKpiDialog}
          onSuccess={() => mutateKpis()}
        />
      )}
    </>
  );
}
